import React, { useEffect, useState, useCallback } from 'react';
import {
  Calendar as CalIcon, ChevronRight, ChevronLeft, Clock, Video, ExternalLink,
  User as UserIcon, Loader2, CheckCircle2, AlertCircle, RefreshCcw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../utils/api';
import { FEATURE_CALENDAR_INTEGRATION } from '../utils/featureFlags';

const STORAGE_KEY = 'mis_reuniones_widget_collapsed';

// Formato HH:mm en zona Chile (los ISO ya vienen con offset)
const fmtHora = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return ''; }
};

const ESTADO_DOT = {
  confirmado: 'bg-emerald-500',
  pendiente: 'bg-amber-400',
  cancelado: 'bg-rose-500',
  reagendar_solicitado: 'bg-orange-500',
};

export default function MisReunionesHoyWidget() {
  // 🔒 Feature flag: integración de Google Calendar pausada temporalmente.
  // (Ver /app/frontend/src/utils/featureFlags.js).
  if (!FEATURE_CALENDAR_INTEGRATION) {
    return null;
  }
  return <MisReunionesHoyWidgetInner />;
}

function MisReunionesHoyWidgetInner() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get('/calendar/today-meetings');
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh cada 5 min
  useEffect(() => {
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const events = data?.events || [];
  const total = events.length;
  const peritumCount = events.filter(e => e.es_peritum).length;
  const isConnected = data?.connected !== false;

  if (collapsed) {
    return (
      <button
        onClick={toggle}
        className="fixed right-0 bottom-24 z-30 bg-slate-900 hover:bg-slate-800 text-white py-3 px-1.5 rounded-l-lg shadow-lg flex flex-col items-center gap-1 transition-all"
        data-testid="mis-reuniones-toggle-open"
        aria-label="Abrir Mis Reuniones de Hoy"
      >
        <ChevronLeft className="w-4 h-4" />
        <CalIcon className="w-4 h-4" />
        {total > 0 && (
          <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
            {total}
          </span>
        )}
      </button>
    );
  }

  return (
    <aside
      className="fixed right-0 top-0 h-screen w-72 bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col"
      data-testid="mis-reuniones-widget"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white p-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <CalIcon className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="font-semibold text-xs leading-tight">Mis Reuniones de Hoy</h3>
            <p className="text-[10px] text-slate-300 truncate">
              {data?.fecha ? new Date(data.fecha + 'T12:00').toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            disabled={loading}
            className="p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50"
            data-testid="mis-reuniones-refresh"
            aria-label="Refrescar"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={toggle}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            data-testid="mis-reuniones-toggle-close"
            aria-label="Cerrar panel"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {isConnected && (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-[11px]">
          <span className="text-slate-600">
            <strong className="text-slate-900 tabular-nums">{total}</strong> evento{total !== 1 ? 's' : ''}
          </span>
          {peritumCount > 0 && (
            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px] h-5">
              {peritumCount} con prospecto
            </Badge>
          )}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {!isConnected ? (
          <div className="p-4 text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-xs text-slate-600">
              Conecta tu Google Calendar para ver tus reuniones de hoy.
            </p>
          </div>
        ) : loading && !data ? (
          <div className="p-6 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <p className="text-[11px] mt-2">Cargando...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <AlertCircle className="w-6 h-6 text-rose-500 mx-auto mb-2" />
            <p className="text-[11px] text-rose-700">{error}</p>
            <Button size="sm" variant="outline" onClick={load} className="mt-2 h-7 text-[11px]">
              Reintentar
            </Button>
          </div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Sin reuniones hoy.</p>
            <p className="text-[10px] text-slate-400 mt-1">Disfruta el día.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map(ev => (
              <EventoCard key={ev.id} ev={ev} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

const EventoCard = ({ ev }) => {
  const verAntecedentes = () => {
    if (!ev.lead_id) return;
    window.open(`/comercial/marketing?lead=${ev.lead_id}`, '_blank', 'noopener');
  };

  const abrirEvento = () => {
    if (ev.html_link) window.open(ev.html_link, '_blank', 'noopener');
  };

  return (
    <li className="p-3 hover:bg-slate-50 transition-colors" data-testid={`reunion-card-${ev.id}`}>
      <div className="flex items-start gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Clock className="w-3 h-3 text-slate-400" />
          <span className="text-[11px] font-semibold tabular-nums text-slate-700">
            {ev.all_day ? 'Todo el día' : `${fmtHora(ev.start)}–${fmtHora(ev.end)}`}
          </span>
        </div>
        {ev.es_peritum && (
          <span
            className={`w-2 h-2 rounded-full mt-0.5 ${ESTADO_DOT[ev.cliente_estado] || 'bg-slate-300'}`}
            title={`Cliente: ${ev.cliente_estado || 'pendiente'}`}
          />
        )}
      </div>

      <button
        onClick={abrirEvento}
        className="text-left w-full"
      >
        <p className="text-xs font-semibold text-slate-900 leading-snug line-clamp-2 hover:text-indigo-700 transition-colors">
          {ev.summary}
        </p>
      </button>

      {ev.es_peritum && ev.lead_nombre && (
        <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
          <UserIcon className="w-3 h-3" />
          <span className="truncate">{ev.lead_nombre}</span>
          {ev.lead_etapa && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto">
              {ev.lead_etapa}
            </Badge>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 mt-2">
        {ev.hangout_link && (
          <a
            href={ev.hangout_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded transition-colors"
            data-testid={`reunion-meet-${ev.id}`}
          >
            <Video className="w-2.5 h-2.5" /> Meet
          </a>
        )}
        {ev.es_peritum && ev.lead_id && (
          <button
            onClick={verAntecedentes}
            className="inline-flex items-center gap-1 text-[10px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1 rounded transition-colors ml-auto"
            data-testid={`reunion-lead-${ev.lead_id}`}
          >
            <ExternalLink className="w-2.5 h-2.5" /> Ver Lead
          </button>
        )}
      </div>
    </li>
  );
};
